<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body">
            <div class="text-center">
              <img src="/src/assets/images/logo/vesproc120.png" alt="VESPROC" class="img-fluid mb-3" />
              <h4 class="f-w-500 mb-1">{{ isAlreadyRegistered ? $t('auth.login.title') : $t('auth.register.title') }}</h4>
              <p v-if="!isAlreadyRegistered" class="mb-3">{{ $t('auth.register.haveAccount') }} <router-link to="/login" class="link-primary">{{ $t('auth.register.login') }}</router-link></p>
              <p v-else class="mb-3">{{ $t('auth.register.alreadyRegisteredMessage', { email: email }) }}</p>
            </div>

            <!-- Alert messages -->
            <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ error }}
              <button type="button" class="btn-close" @click="error = null"></button>
            </div>

            <!-- Registration fields - only show when not already registered -->
            <div v-if="!isAlreadyRegistered">
              <!-- Company Name -->
              <div class="mb-3">
                <input
                  type="text"
                  class="form-control"
                  :placeholder="$t('auth.register.companyCode')"
                  v-model="companyCode"
                />
                <div v-if="hasFieldError('companyCode')" class="text-danger text-start">
                  {{ getFieldError('companyCode') }}
                </div>
              </div>

              <div class="row">
                <div class="col-sm-6">
                  <div class="mb-3">
                    <input
                      type="text"
                      class="form-control"
                      :placeholder="$t('auth.register.firstName')"
                      v-model="firstName"
                    />
                    <div v-if="hasFieldError('firstName')" class="text-danger text-start">
                      {{ getFieldError('firstName') }}
                    </div>
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="mb-3">
                    <input
                      type="text"
                      class="form-control"
                      :placeholder="$t('auth.register.lastName')"
                      v-model="lastName"
                    />
                    <div v-if="hasFieldError('lastName')" class="text-danger text-start">
                      {{ getFieldError('lastName') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Email field -->
            <div class="mb-3">
              <div class="input-group">
                <input
                  type="email"
                  class="form-control"
                  :placeholder="isAlreadyRegistered ? $t('auth.login.email') : $t('auth.register.email')"
                  v-model="email"
                  :disabled="isEmailLocked || isDecodingEmail"
                />
                <span v-if="isDecodingEmail" class="input-group-text">
                  <span class="spinner-border spinner-border-sm"></span>
                </span>
              </div>
              <div v-if="hasFieldError('email')" class="text-danger text-start">
                {{ getFieldError('email') }}
              </div>
              <div v-if="isEmailLocked && !isAlreadyRegistered" class="text-muted small mt-1">
                {{ $t('auth.register.emailPrefilledFromLink') }}
              </div>
              <div v-if="isDecodingEmail" class="text-muted small mt-1">
                {{ $t('auth.register.decodingEmail') }}
              </div>
            </div>

            <!-- Phone field - only show for registration -->
            <div v-if="!isAlreadyRegistered" class="mb-3">
              <div class="input-group">
                <!--
                  The native select sits transparently over a compact "TR +90" label: the
                  closed control stays narrow, while the open list still shows full names.
                -->
                <div class="input-group-text phone-country">
                  <span>{{ phoneCountry }} {{ selectedDialCode }}</span>
                  <i class="ph-duotone ph-caret-down ms-1"></i>
                  <select
                    v-model="phoneCountry"
                    class="phone-country-native"
                    :aria-label="$t('auth.register.phoneCountry')"
                  >
                    <option v-for="country in phoneCountries" :key="country.iso2" :value="country.iso2">
                      {{ country.name }} ({{ country.dialCode }})
                    </option>
                  </select>
                </div>
                <input
                  type="tel"
                  inputmode="tel"
                  class="form-control"
                  autocomplete="tel-national"
                  :maxlength="PHONE_MAX_LENGTH"
                  :placeholder="$t('auth.register.phone')"
                  :value="phone"
                  @input="onPhoneInput"
                  @blur="onPhoneBlur"
                />
              </div>
              <div v-if="hasFieldError('phone')" class="text-danger text-start">
                {{ getFieldError('phone') }}
              </div>
            </div>

            <!-- Password field -->
            <div class="mb-3">
              <div class="position-relative">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control pe-5"
                  :placeholder="isAlreadyRegistered ? $t('auth.login.password') : $t('auth.register.password')"
                  v-model="password"
                />
                <button
                  type="button"
                  class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                  @click="showPassword = !showPassword"
                  style="z-index: 10; border: none; background: none;"
                >
                  <i :class="showPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                </button>
              </div>
              <div v-if="hasFieldError('password')" class="text-danger text-start">
                {{ getFieldError('password') }}
              </div>
              <!-- Forgot password link - only show when already registered -->
              <div v-if="isAlreadyRegistered" class="text-end mt-2">
                <a href="#" class="link-primary" @click.prevent="handleForgotPassword">
                  <h6 class="f-w-400 mb-0">{{ $t('auth.login.forgotPassword') }}</h6>
                </a>
              </div>
              <!-- Password strength indicator - only show for registration -->
              <div v-if="!isAlreadyRegistered && password" class="mt-2">
                <div class="progress" style="height: 6px;">
                  <div
                    class="progress-bar"
                    :class="passwordStrengthClass"
                    :style="{ width: passwordStrengthPercent + '%' }"
                  ></div>
                </div>
                <small :class="passwordStrengthTextClass">
                  {{ passwordStrengthText }}
                </small>
                <div
                  class="small mt-1"
                  :class="password.length < PASSWORD_MIN_LENGTH ? 'text-danger' : 'text-muted'"
                >
                  {{ password.length < PASSWORD_MIN_LENGTH
                    ? $t('auth.register.passwordMinLength', { min: PASSWORD_MIN_LENGTH })
                    : $t('auth.register.passwordPolicyHint', { min: PASSWORD_MIN_LENGTH }) }}
                </div>
                <!-- zxcvbn feedback while the password is below the bar -->
                <div v-if="!meetsStrengthBar && passwordStrength.feedback.warning" class="mt-1">
                  <small class="text-warning">
                    <i class="ph-duotone ph-warning me-1"></i>
                    {{ passwordStrength.feedback.warning }}
                  </small>
                </div>
                <div v-if="!meetsStrengthBar && passwordStrength.feedback.suggestions.length > 0" class="mt-1">
                  <small class="text-muted">
                    <i class="ph-duotone ph-lightbulb me-1"></i>
                    {{ passwordStrength.feedback.suggestions[0] }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Confirm password - only show for registration -->
            <div v-if="!isAlreadyRegistered" class="mb-3">
              <div class="position-relative">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="form-control pe-5"
                  :placeholder="$t('auth.register.confirmPassword')"
                  v-model="confirmPassword"
                />
                <button
                  type="button"
                  class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                  @click="showConfirmPassword = !showConfirmPassword"
                  style="z-index: 10; border: none; background: none;"
                >
                  <i :class="showConfirmPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                </button>
              </div>
              <div v-if="hasFieldError('confirmPassword')" class="text-danger text-start">
                {{ getFieldError('confirmPassword') }}
              </div>
            </div>

            <!--
              Honeypot. Kept out of the tab order and hidden from assistive technology, so
              the only thing that can reach it is something filling in fields it cannot see.
              Off-screen rather than display:none, which the cruder scripts do skip.
            -->
            <div v-if="!isAlreadyRegistered" class="hp-field" aria-hidden="true">
              <label :for="honeypotFieldName">{{ honeypotFieldName }}</label>
              <input
                :id="honeypotFieldName"
                :name="honeypotFieldName"
                v-model="honeypotValue"
                type="text"
                tabindex="-1"
                autocomplete="off"
              />
            </div>

            <!-- Consents. KVKK is required; marketing is not, and the two are never one tick. -->
            <div v-if="!isAlreadyRegistered" class="mb-3">
              <div class="form-check">
                <input
                  id="kvkkConsent"
                  v-model="kvkkConsent"
                  class="form-check-input input-primary"
                  type="checkbox"
                />
                <label class="form-check-label" :class="hasFieldError('kvkkConsent') ? 'text-danger' : 'text-muted'" for="kvkkConsent">
                  {{ $t('auth.register.kvkkConsent') }}
                  <a href="#" class="link-primary" @click.prevent="openTermsModal">
                    {{ $t('auth.register.termsAndConditions') }}
                  </a>
                </label>
                <div v-if="hasFieldError('kvkkConsent')" class="text-danger small">
                  {{ getFieldError('kvkkConsent') }}
                </div>
              </div>

              <div class="form-check mt-2">
                <input
                  id="marketingConsent"
                  v-model="marketingConsent"
                  class="form-check-input input-primary"
                  type="checkbox"
                />
                <label class="form-check-label text-muted" for="marketingConsent">
                  {{ $t('auth.register.marketingConsent') }}
                </label>
              </div>
            </div>

            <div v-if="!isAlreadyRegistered" class="mb-3">
              <CaptchaField ref="captchaField" @update:token="captchaToken = $event" />
              <div v-if="hasFieldError('captcha')" class="text-danger text-start small mt-1">
                {{ getFieldError('captcha') }}
              </div>
            </div>

            <!--
              The button is only blocked while something is genuinely in flight. Disabling
              it for an invalid form is a trap: validateForm() is what produces the
              per-field messages, so a disabled button refuses the supplier with no way to
              find out why — and the minimum-length rule is invisible next to a strength
              meter reading "Very Strong".
            -->
            <div class="d-grid mt-4">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="isLoading || isDecodingEmail"
                @click="handleButtonClick"
              >
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                {{ isAlreadyRegistered ? $t('auth.login.loginButton') : $t('auth.register.registerButton') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  </div>

  <!-- Terms and Conditions Modal -->
  <div class="modal fade" :class="{ show: showTermsModal }" :style="{ display: showTermsModal ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('auth.register.termsAndConditions') }}</h5>
          <button type="button" class="btn-close" @click="closeTermsModal"></button>
        </div>
        <div class="modal-body">
          <div class="terms-content">
            <h6>{{ $t('auth.terms.acceptance') }}</h6>
            <p>{{ $t('auth.terms.acceptanceText') }}</p>

            <h6>{{ $t('auth.terms.services') }}</h6>
            <p>{{ $t('auth.terms.servicesText') }}</p>

            <h6>{{ $t('auth.terms.userAccount') }}</h6>
            <p>{{ $t('auth.terms.userAccountText') }}</p>

            <h6>{{ $t('auth.terms.privacy') }}</h6>
            <p>{{ $t('auth.terms.privacyText') }}</p>

            <h6>{{ $t('auth.terms.liability') }}</h6>
            <p>{{ $t('auth.terms.liabilityText') }}</p>

            <h6>{{ $t('auth.terms.modifications') }}</h6>
            <p>{{ $t('auth.terms.modificationsText') }}</p>

            <h6>{{ $t('auth.terms.contact') }}</h6>
            <p>{{ $t('auth.terms.contactText') }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeTermsModal">
            {{ $t('common.actions.close') }}
          </button>
          <button type="button" class="btn btn-primary" @click="acceptTermsFromModal">
            {{ $t('auth.register.acceptAndClose') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal backdrop -->
  <div v-if="showTermsModal" class="modal-backdrop fade show" @click="closeTermsModal"></div>
</template>

<script setup>
// 1. Vue core
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

// 2. Third-party
import Swal from 'sweetalert2'

// 3. Services & composables
import authService from '@/services/authService'
import passwordStrengthService from '@/services/passwordStrengthService'
import { useEmailPolicy } from '@/composables/useEmailPolicy'
import { useHoneypot } from '@/composables/useHoneypot'
import { usePhoneCountries } from '@/composables/usePhoneCountries'
import { useFeatureFlags } from '@/composables/useFeatureFlags'

// 4. Components
import AuthFooter from '@/components/auth/AuthFooter.vue'
import CaptchaField from '@/components/auth/CaptchaField.vue'

// 5. Constants
import { PASSWORD_MIN_LENGTH, PASSWORD_MIN_STRENGTH_SCORE } from '@/constants/registration'

const store = useStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { features } = useFeatureFlags()

const { classify } = useEmailPolicy()

// Destructured rather than kept as one object: nested refs on a plain object are not
// unwrapped in templates, so `honeypot.honeypotValue` would bind the ref itself.
const {
  fieldName: honeypotFieldName,
  honeypotValue,
  reset: resetHoneypot,
  payload: honeypotPayload
} = useHoneypot()

const { phoneCountries, guessDefaultCountry, parsePhone, formatAsYouType } = usePhoneCountries()

const companyCode = ref('')
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const phoneCountry = ref(guessDefaultCountry())
const phone = ref('')
const password = ref('')
const confirmPassword = ref('')
const kvkkConsent = ref(false)
const marketingConsent = ref(false)
const captchaToken = ref('')
const captchaField = ref(null)

const error = ref(null)
const isLoading = ref(false)
const showTermsModal = ref(false)
const isEmailFromLink = ref(false)
const isDecodingEmail = ref(false)
const isAlreadyRegistered = ref(false)
const suppressValidation = ref(false)

const showPassword = ref(false)
const showConfirmPassword = ref(false)

const validationErrors = ref({})

// URL parameters carried by an invitation link
const urlRefs = ref({ u: null, k: null })

// Advanced password strength calculation using zxcvbn-ts
const passwordStrength = computed(() => {
  const pwd = password.value
  if (!pwd) {
    return {
      score: 0,
      strength: 'veryWeak',
      text: '',
      class: 'bg-secondary',
      textClass: 'text-muted',
      percentage: 0,
      feedback: { warning: '', suggestions: [] }
    }
  }

  // Prepare user inputs to penalize personal info in passwords
  const userInputs = []
  if (email.value) {
    const emailParts = email.value.split('@')
    userInputs.push(emailParts[0])
    userInputs.push(...emailParts[0].split('.'))
  }
  if (firstName.value) userInputs.push(firstName.value.toLowerCase())
  if (lastName.value) userInputs.push(lastName.value.toLowerCase())
  if (companyCode.value) userInputs.push(companyCode.value.toLowerCase())

  userInputs.push('nozzle', 'supplier', 'portal', 'password', 'vesproc')

  const evaluation = passwordStrengthService.evaluatePassword(pwd, userInputs)

  return {
    score: evaluation.score,
    strength: evaluation.strength,
    text: passwordStrengthService.getLocalizedStrength(evaluation.strength, t),
    class: passwordStrengthService.getStrengthClass(evaluation.score),
    textClass: passwordStrengthService.getStrengthTextClass(evaluation.score),
    percentage: passwordStrengthService.getStrengthPercentage(evaluation.score),
    feedback: evaluation.feedback,
    guesses: evaluation.guesses,
    crackTime: evaluation.crackTimesDisplay,
    calcTime: evaluation.calculation_time
  }
})

const passwordStrengthPercent = computed(() => passwordStrength.value.percentage)
const passwordStrengthClass = computed(() => passwordStrength.value.class)
const passwordStrengthText = computed(() => passwordStrength.value.text)
const passwordStrengthTextClass = computed(() => passwordStrength.value.textClass)

const meetsStrengthBar = computed(() =>
  passwordStrengthService.meetsMinimumStrength(passwordStrength.value.score, PASSWORD_MIN_STRENGTH_SCORE)
)


/** Longest legitimate entry: 15 digits (E.164) plus separators and a leading "+". */
const PHONE_MAX_LENGTH = 20

/**
 * Keeps only what a phone number can contain: digits, spaces, "-", "(", ")" and a
 * single leading "+". Letters and anything else are dropped as they are typed or pasted.
 */
const sanitizePhone = value => {
  const trimmed = value.trimStart()
  const hasPlus = trimmed.startsWith('+')
  const body = trimmed.replaceAll(/[^\d\s()-]/g, '')
  return ((hasPlus ? '+' : '') + body).slice(0, PHONE_MAX_LENGTH)
}

const onPhoneInput = event => {
  const input = event.target
  let value = sanitizePhone(input.value)

  // Group digits as they are typed ("538 290 39 26"). Only when typing at the end:
  // reformatting on a deletion would put back the space the supplier just removed, and
  // mid-string edits would throw the caret to the end.
  const typingAtEnd = event.inputType?.startsWith('insert') && input.selectionStart === input.value.length
  if (typingAtEnd) value = formatAsYouType(value, phoneCountry.value)

  // Write back even when the model is unchanged, so a rejected keystroke leaves no trace
  input.value = value
  phone.value = value
  // Once the supplier fixes the number, drop the complaint without waiting for blur
  if (hasFieldError('phone')) validateField('phone', value)
}

/**
 * On leaving the field a valid number is rewritten in its standard form next to the
 * dial code: "05382903926", "5382903926" and "+90 538 290 39 26" all become
 * "538 290 39 26". A number typed with another country's code switches the selector.
 */
const onPhoneBlur = () => {
  if (!phone.value) return
  const parsed = parsePhone(phone.value, phoneCountry.value)
  if (parsed.valid) {
    if (parsed.country && parsed.dialCode !== selectedDialCode.value) phoneCountry.value = parsed.country
    phone.value = parsed.national
  }
  validateField('phone', phone.value)
}

const selectedDialCode = computed(() =>
  phoneCountries.value.find(country => country.iso2 === phoneCountry.value)?.dialCode || ''
)

const emailPolicy = computed(() => classify(email.value))

/**
 * An address carried in by an invitation link is normally read-only — it is what the
 * buyer vouched for. It unlocks when it fails the work-email policy, because a disabled
 * field holding a rejected address is a dead end: the supplier can neither submit it nor
 * replace it.
 */
const isEmailLocked = computed(() =>
  isEmailFromLink.value && (isAlreadyRegistered.value || emailPolicy.value.ok)
)

/**
 * Without a site key the widget never renders, so the captcha field is not required —
 * otherwise a developer with no Google account faces a form that can never be submitted.
 */
const isCaptchaEnabled = computed(() => Boolean(features.captchaSiteKey))


// Decode email from URL parameters
const decodeEmailFromLink = async (unq, key) => {
  try {
    isDecodingEmail.value = true
    const response = await authService.decodeRegistrationEmail(unq, key)

    if (response && response.email) {
      // Temporarily suppress validation to avoid loops
      suppressValidation.value = true
      await nextTick()

      if (response.alreadyRegister) {
        isAlreadyRegistered.value = true
      }

      email.value = response.email
      isEmailFromLink.value = true
      error.value = null

      await nextTick()
      suppressValidation.value = false
    } else {
      error.value = t('auth.register.emailDecodeError')
      isEmailFromLink.value = false
    }
  } catch (err) {
    console.error('Email decode error:', err)

    if (err.response?.status === 404) {
      error.value = t('auth.register.invalidInvitationLink')
    } else if (err.response?.status >= 500) {
      error.value = t('auth.register.serverError')
    } else {
      error.value = t('auth.register.emailDecodeError')
    }

    isEmailFromLink.value = false
    email.value = ''
    suppressValidation.value = false
  } finally {
    isDecodingEmail.value = false
  }
}

onMounted(async () => {
  await nextTick()

  const unq = route.query.unq
  const key = route.query.key

  if (unq && key) {
    urlRefs.value = { u: unq, k: key }
    setTimeout(async () => {
      await decodeEmailFromLink(unq, key)
    }, 300)
  }
})

const validateEmailField = (value, errors) => {
  if (!value) {
    errors.push(t('auth.register.emailRequired'))
    return
  }
  if (!/\S+@\S+\.\S+/.test(value)) {
    errors.push(t('auth.register.emailInvalid'))
    return
  }
  // Login mode reaches this through an invitation link for an address that already
  // exists; re-running the work-email policy on it would block a legitimate sign-in.
  if (isAlreadyRegistered.value) return

  const policy = classify(value)
  if (policy.reason === 'disposable') errors.push(t('auth.register.emailDisposable'))
  if (policy.reason === 'freeMail') errors.push(t('auth.register.emailFreeMail'))
}

const validatePasswordField = (value, errors) => {
  if (!value) {
    errors.push(t('auth.register.passwordRequired'))
    return
  }
  if (value.length < PASSWORD_MIN_LENGTH) {
    errors.push(t('auth.register.passwordMinLength', { min: PASSWORD_MIN_LENGTH }))
    return
  }
  if (!meetsStrengthBar.value) {
    errors.push(t('auth.register.passwordTooWeak'))
  }
}

const FIELD_VALIDATORS = {
  companyCode: (value, errors) => {
    if (!value) errors.push(t('auth.register.companyCodeRequired'))
  },
  firstName: (value, errors) => {
    if (!value) errors.push(t('auth.register.nameRequired'))
  },
  lastName: (value, errors) => {
    if (!value) errors.push(t('auth.register.nameRequired'))
  },
  phone: (value, errors) => {
    if (!value) errors.push(t('auth.register.phoneRequired'))
    else if (!parsePhone(value, phoneCountry.value).valid) errors.push(t('auth.register.phoneInvalid'))
  },
  email: validateEmailField,
  password: validatePasswordField,
  confirmPassword: (value, errors) => {
    if (!value) errors.push(t('auth.register.confirmRequired'))
    else if (value !== password.value) errors.push(t('auth.register.passwordsNoMatch'))
  },
  kvkkConsent: (value, errors) => {
    if (!value) errors.push(t('auth.register.kvkkRequired'))
  },
  captcha: (value, errors) => {
    if (isCaptchaEnabled.value && !value) errors.push(t('auth.register.captchaRequired'))
  }
}

/** Fields checked in login mode; the registration rules do not apply there. */
const LOGIN_MODE_FIELDS = ['email', 'password']

const REGISTRATION_FIELDS = [
  'companyCode',
  'firstName',
  'lastName',
  'email',
  'phone',
  'password',
  'confirmPassword',
  'kvkkConsent',
  'captcha'
]

const FIELD_VALUES = {
  companyCode: () => companyCode.value,
  firstName: () => firstName.value,
  lastName: () => lastName.value,
  email: () => email.value,
  phone: () => phone.value,
  password: () => password.value,
  confirmPassword: () => confirmPassword.value,
  kvkkConsent: () => kvkkConsent.value,
  captcha: () => captchaToken.value
}

const validateField = (field, value) => {
  if (suppressValidation.value) return true

  const errors = []

  // In login mode only the credentials matter, and only presence is checked.
  if (isAlreadyRegistered.value) {
    if (!LOGIN_MODE_FIELDS.includes(field)) return true
    if (field === 'email') validateEmailField(value, errors)
    if (field === 'password' && !value) errors.push(t('auth.register.passwordRequired'))
  } else {
    FIELD_VALIDATORS[field]?.(value, errors)
  }

  validationErrors.value[field] = errors
  return errors.length === 0
}

const validateForm = () => {
  if (suppressValidation.value) return true

  validationErrors.value = {}
  const fields = isAlreadyRegistered.value ? LOGIN_MODE_FIELDS : REGISTRATION_FIELDS

  // Every field is validated, not short-circuited: the form shows all its complaints at
  // once rather than making the supplier discover them one submit at a time.
  return fields.reduce((valid, field) => validateField(field, FIELD_VALUES[field]()) && valid, true)
}

const hasFieldError = field =>
  !suppressValidation.value && validationErrors.value[field] && validationErrors.value[field].length > 0

const getFieldError = field => validationErrors.value[field] && validationErrors.value[field][0]

// Live validation: warn as soon as the two passwords diverge, without waiting for submit
watch(confirmPassword, newValue => {
  if (suppressValidation.value || isAlreadyRegistered.value) return
  if (!newValue) {
    validationErrors.value['confirmPassword'] = []
    return
  }
  validateField('confirmPassword', newValue)
})

watch(password, () => {
  if (suppressValidation.value || isAlreadyRegistered.value) return
  if (confirmPassword.value) {
    validateField('confirmPassword', confirmPassword.value)
  } else {
    validationErrors.value['confirmPassword'] = []
  }
})

// A number valid for one country may not be for another, so re-check on country change
watch(phoneCountry, () => {
  if (suppressValidation.value || isAlreadyRegistered.value || !phone.value) return
  validateField('phone', phone.value)
})

// Clear the address complaint as soon as the supplier switches to a work address
watch(email, () => {
  if (suppressValidation.value || isAlreadyRegistered.value) return
  if (validationErrors.value['email']?.length && emailPolicy.value.ok) {
    validationErrors.value['email'] = []
  }
})

const register = async () => {
  error.value = null

  if (!validateForm()) return

  isLoading.value = true

  try {
    const registrationData = {
      tenantName: companyCode.value,
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      // E.164 ("+905321234567"), so the stored number carries its country with it
      phoneNumber: parsePhone(phone.value, phoneCountry.value).e164,
      password: password.value,
      kvkkConsent: kvkkConsent.value,
      marketingConsent: marketingConsent.value,
      captchaToken: captchaToken.value,
      ...honeypotPayload()
    }

    if (urlRefs.value.u && urlRefs.value.k) {
      registrationData.refs = { u: urlRefs.value.u, k: urlRefs.value.k }
    }

    await authService.register(registrationData)

    // The account cannot sign in until its address is verified, so the next step is the
    // mailbox, not the login form. Kept before the form is cleared below.
    const registeredEmail = email.value

    companyCode.value = ''
    firstName.value = ''
    lastName.value = ''
    email.value = ''
    phone.value = ''
    password.value = ''
    confirmPassword.value = ''
    kvkkConsent.value = false
    marketingConsent.value = false
    validationErrors.value = {}

    router.push({ name: 'CheckEmail', state: { email: registeredEmail } })
  } catch (err) {
    error.value = err.response?.data?.error?.message ||
                  err.response?.data?.message ||
                  t('common.notification.registerError')
    console.error('Register error:', err)
  } finally {
    // The token is spent whether or not the call succeeded, so a retry needs a new
    // challenge and a clock that starts from this attempt.
    captchaToken.value = ''
    captchaField.value?.reset()
    resetHoneypot()
    isLoading.value = false
  }
}

const loginWithEncryptedData = async () => {
  error.value = null

  if (!validateForm()) return

  isLoading.value = true

  try {
    const loginData = {
      email: email.value,
      password: password.value,
      refs: { u: urlRefs.value.u, k: urlRefs.value.k }
    }

    const result = await authService.login(loginData)

    await store.dispatch('auth/setAuthData', result)

    store.dispatch('notify', {
      message: t('common.notification.loginSuccess'),
      type: 'success'
    })

    router.push('/')
  } catch (err) {
    error.value = err.response?.data?.error?.message ||
                  err.response?.data?.message ||
                  t('common.notification.loginError')
    console.error('Login error:', err)
  } finally {
    isLoading.value = false
  }
}

const handleButtonClick = () => {
  if (isAlreadyRegistered.value) {
    loginWithEncryptedData()
  } else {
    register()
  }
}

const openTermsModal = () => {
  showTermsModal.value = true
}

const closeTermsModal = () => {
  showTermsModal.value = false
}

/** Reading the terms to the end is itself the consent the modal collects. */
const acceptTermsFromModal = () => {
  kvkkConsent.value = true
  showTermsModal.value = false
}

const handleForgotPassword = async () => {
  if (!email.value) {
    error.value = t('auth.forgotPassword.emailRequired')
    return
  }

  const result = await Swal.fire({
    title: t('auth.forgotPassword.confirmTitle'),
    text: t('auth.forgotPassword.confirmMessage', { email: email.value }),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('common.yes'),
    cancelButtonText: t('common.no'),
    confirmButtonColor: '#00A0DF',
    cancelButtonColor: '#6c757d'
  })

  if (!result.isConfirmed) return

  isLoading.value = true
  error.value = null

  try {
    await authService.requestPasswordReset(email.value)

    await Swal.fire({
      title: t('auth.forgotPassword.successTitle'),
      text: t('auth.forgotPassword.success'),
      icon: 'success',
      confirmButtonColor: '#00A0DF'
    })
  } catch (err) {
    const errorMessage = err.response?.data?.error?.message ||
                         err.response?.data?.message ||
                         t('auth.forgotPassword.error')

    await Swal.fire({
      title: t('common.notification.error'),
      text: errorMessage,
      icon: 'error',
      confirmButtonColor: '#00A0DF'
    })

    console.error('Password reset error:', err)
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.auth-main.v1 {
  background-color: #f2f2f2;
  background-image: url('/src/assets/images/logo/worldmap.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.auth-form {
  max-width: 460px;
}

.card {
  margin: 1.5rem 0 !important;
}

.card-body {
  padding: 1.75rem 1.5rem;
}

.mb-3 {
  margin-bottom: 0.875rem !important;
}

.text-center img {
  max-width: 90px;
  margin-bottom: 1rem !important;
}

.text-center h4 {
  font-size: 1.35rem;
  margin-bottom: 0.5rem !important;
}

.text-center p {
  margin-bottom: 1.25rem !important;
}

.form-control {
  height: 38px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

.phone-country {
  position: relative;
  height: 38px;
  padding: 0 0.75rem;
  font-size: 0.9rem;
  cursor: pointer;
}

.phone-country:focus-within {
  border-color: var(--bs-primary);
}

.phone-country-native {
  position: absolute;
  inset: 0;
  width: 100%;
  opacity: 0;
  cursor: pointer;
}

.btn {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

/* Honeypot: off the screen rather than display:none, which some scripts skip. */
.hp-field {
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
</style>
